import { FormAutoGenerated, Wrapper, useStyletron } from '@visualfabriq/vf-ui-kit';
import {
  FieldItem,
  FormLayoutRow,
} from '@visualfabriq/vf-ui-kit/dist/components/molecules/form/form-auto-generated/types';
import { useEffect, useState } from 'react';
import * as awsCronValidate from 'src/utils/awsCron/validate';
import {
  dayOfMonthAllowedCharacters,
  dayOfWeekAllowedCharacters,
  hoursAllowedCharacters,
  minutesAllowedCharacters,
  monthAllowedCharacters,
  yearAllowedCharacters,
} from './constants';

type Props = {
  value: string;
  tzOnly: boolean;
  timeZoneInput: JSX.Element;
  onChange: (value: string) => void;
  onValidChange: (valid: boolean) => void;
};

export function CronSchedule(props: Props) {
  const { value, timeZoneInput, tzOnly, onChange, onValidChange } = props;
  const [hoursMinsValid, setHoursMinsValid] = useState(false);
  const [daysMonthsYearsValid, setDaysMonthsYearsValid] = useState(false);
  const [css, theme] = useStyletron();

  useEffect(() => onValidChange(hoursMinsValid && daysMonthsYearsValid), [hoursMinsValid, daysMonthsYearsValid]);

  const [minutes, hours, dayOfMonth, month, dayOfWeek, year] = value
    .split(' ')
    .map((value) => (value === 'unset' ? null : value));
  const cron = { minutes, hours, dayOfMonth, month, dayOfWeek, year };

  const handleMinsHoursChange = (event: Partial<Record<string, unknown>>) => {
    const newValue = [event.minutes, event.hours, cron.dayOfMonth, cron.month, cron.dayOfWeek, cron.year].join(' ');
    onChange(newValue);
  };

  const handleDaysMonthsYearsChange = (event: Partial<Record<string, unknown>>) => {
    const newValue = [
      cron.minutes?.trim(),
      cron.hours?.trim(),
      (event.dayOfMonth as string)?.trim(),
      (event.month as string)?.trim(),
      (event.dayOfWeek as string)?.trim(),
      (event.year as string)?.trim(),
    ].join(' ');
    onChange(newValue);
  };

  return (
    <Wrapper direction="column">
      {!tzOnly && (
        <FormAutoGenerated
          fields={daysMonthsYearsFields}
          layout={daysMonthsYearsLayout}
          onDataChange={handleDaysMonthsYearsChange}
          onFormValidChange={setDaysMonthsYearsValid}
          initData={cron}
        ></FormAutoGenerated>
      )}
      <Wrapper gap={600}>
        <Wrapper className={css({ flex: 1, paddingRight: theme.sizing.scale600 })} direction="column">
          {timeZoneInput}
        </Wrapper>
        {!tzOnly && (
          <Wrapper className={css({ flex: 1 })} direction="column">
            <FormAutoGenerated
              fields={minsHoursFields}
              layout={minsHoursLayout}
              onDataChange={handleMinsHoursChange}
              onFormValidChange={setHoursMinsValid}
              initData={cron}
            ></FormAutoGenerated>
          </Wrapper>
        )}
      </Wrapper>
    </Wrapper>
  );
}

type CreateCronValidator = (
  validator: (value: string) => boolean,
  allowedCharacters: string,
) => (value: string) => string | boolean;

const createCronValueGetError: CreateCronValidator = (validator, allowedCharacters) => (value) =>
  validator(value) ? false : `Invalid characters. Allowed characters: ${allowedCharacters}`;

const minsHoursLayout: FormLayoutRow[] = [
  [
    {
      cols: [2, 3, 6],
      field: 'hours',
    },
    {
      cols: [2, 3, 6],
      field: 'minutes',
    },
  ],
];

const daysMonthsYearsLayout: FormLayoutRow[] = [
  [
    {
      cols: [1, 1, 3],
      field: 'dayOfWeek',
    },
    {
      cols: [1, 1, 3],
      field: 'dayOfMonth',
    },
    {
      cols: [1, 1, 3],
      field: 'month',
    },
    {
      cols: [1, 1, 3],
      field: 'year',
    },
  ],
];

const dirtyHackToKeepFormInvalidWhenDataIsUndefinedButDontShowErrorMessage = {
  // we don't validate if it null expect required.value ===  true
  // https://github.com/visualfabriq/mfe-utils/blob/45fa37d307591ab28d5e9b7247c2ad62bef00f69/src/data/validate/utils.ts#L118
  required: {
    value: true,
    message: '',
  },
};

const minsHoursFields: FieldItem[] = [
  {
    field_desc: 'Minutes',
    field_id: 'minutes',
    data_type: 'text',
    is_obligatory_field: true,
    tooltip: minutesAllowedCharacters,
    validations: {
      ...dirtyHackToKeepFormInvalidWhenDataIsUndefinedButDontShowErrorMessage,
      custom: {
        getError: createCronValueGetError(awsCronValidate.validateMinutes, minutesAllowedCharacters),
      },
    },
  },
  {
    field_desc: 'Hours',
    field_id: 'hours',
    data_type: 'text',
    is_obligatory_field: true,
    tooltip: hoursAllowedCharacters,
    validations: {
      ...dirtyHackToKeepFormInvalidWhenDataIsUndefinedButDontShowErrorMessage,
      custom: {
        getError: createCronValueGetError(awsCronValidate.validateHours, hoursAllowedCharacters),
      },
    },
  },
];

const daysMonthsYearsFields: FieldItem[] = [
  {
    field_desc: 'Day of Week',
    field_id: 'dayOfWeek',
    data_type: 'text',
    is_obligatory_field: true,
    tooltip: dayOfWeekAllowedCharacters,
    validations: {
      ...dirtyHackToKeepFormInvalidWhenDataIsUndefinedButDontShowErrorMessage,
      custom: {
        getError: createCronValueGetError(awsCronValidate.validateDayOfWeek, dayOfWeekAllowedCharacters),
      },
    },
  },
  {
    field_desc: 'Day of Month',
    field_id: 'dayOfMonth',
    data_type: 'text',
    is_obligatory_field: true,
    tooltip: dayOfMonthAllowedCharacters,
    validations: {
      ...dirtyHackToKeepFormInvalidWhenDataIsUndefinedButDontShowErrorMessage,
      custom: {
        getError: createCronValueGetError(awsCronValidate.validateDayOfMonth, dayOfMonthAllowedCharacters),
      },
    },
  },
  {
    field_desc: 'Month',
    field_id: 'month',
    data_type: 'text',
    is_obligatory_field: true,
    tooltip: monthAllowedCharacters,
    validations: {
      ...dirtyHackToKeepFormInvalidWhenDataIsUndefinedButDontShowErrorMessage,
      custom: {
        getError: createCronValueGetError(awsCronValidate.validateMonth, monthAllowedCharacters),
      },
    },
  },

  {
    field_desc: 'Year',
    field_id: 'year',
    data_type: 'text',
    is_obligatory_field: true,
    tooltip: yearAllowedCharacters,
    validations: {
      ...dirtyHackToKeepFormInvalidWhenDataIsUndefinedButDontShowErrorMessage,
      custom: {
        getError: createCronValueGetError(awsCronValidate.validateYear, yearAllowedCharacters),
      },
    },
  },
];
